// src/map-manager.ts
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { TILE_PRESETS, type MapStyle, type TilePreset } from './config';
import { isCarColor } from './car-colors';
import { carMarkerSvg, type CarShape, DEFAULT_CAR_SHAPE, isCarShape } from './car-shapes';
import type { LocationMessage } from './protocol';

// Fix Leaflet's default icon path issue with Vite
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png?url';
import markerIcon from 'leaflet/dist/images/marker-icon.png?url';
import markerShadow from 'leaflet/dist/images/marker-shadow.png?url';
// @ts-expect-error — private Leaflet API
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

export const SELF_COLOR = '#2456ff'; // bold American blue

const TRAIL_OPACITY = 0.6;
const STALE_TRAIL_OPACITY = 0.15;
const STALE_MARKER_OPACITY = 0.35;

/**
 * Shift a peer's trail timestamps onto the local wall clock, anchoring the
 * newest point at "now". Peers stamp entries with their own Date.now(), so
 * clock skew (or an old client still shipping the Tesla browser's non-Unix
 * pos.timestamp epoch) would otherwise break the rolling-window cutoff.
 * Relative ages within one peer's trail are preserved; each incoming message
 * carries the full trail, so re-anchoring per message loses nothing.
 */
export function rebaseTrailTimestamps(
  trail: Array<{ lat: number; lng: number; timestamp: number }>,
  now = Date.now(),
): Array<{ lat: number; lng: number; timestamp: number }> {
  let newest = -Infinity;
  for (const p of trail) {
    if (Number.isFinite(p.timestamp) && p.timestamp > newest) newest = p.timestamp;
  }
  if (!Number.isFinite(newest)) {
    return trail.map(p => ({ ...p, timestamp: now }));
  }
  const offset = now - newest;
  return trail.map(p => ({
    ...p,
    timestamp: Number.isFinite(p.timestamp) ? p.timestamp + offset : now,
  }));
}

/** Escape peer-controlled strings before they reach Leaflet popup/tooltip HTML. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Teardrop pin used for destination markers */
export function makeDestinationIcon(color: string): L.DivIcon {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="34" viewBox="0 0 26 34">
    <path d="M13 0C5.8 0 0 5.8 0 13c0 9.75 13 21 13 21s13-11.25 13-21C26 5.8 20.2 0 13 0z"
      fill="${color}" opacity="0.9"/>
    <circle cx="13" cy="13" r="5" fill="white" opacity="0.85"/>
  </svg>`;
  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [26, 34],
    iconAnchor: [13, 34],
    popupAnchor: [0, -34],
  });
}
// Distinct, dark-and-light-tile-friendly hues. With a big convoy the palette
// wraps, so the more entries the better — and none should collide with
// SELF_COLOR's blue or sit too close to a neighbor.
export const PEER_COLORS = [
  '#e6194b', // crimson
  '#f58231', // orange
  '#ffe119', // yellow
  '#3cb44b', // green
  '#42d4f4', // sky
  '#911eb4', // violet
  '#f032e6', // magenta
  '#bfef45', // lime
  '#fabed4', // pink
  '#469990', // teal
  '#dcbeff', // lavender
  '#9a6324', // umber
  '#ffd8b1', // apricot
  '#808000', // olive
  '#aaffc3', // mint
  '#c62828', // brick
  '#b45309', // amber
  '#15803d', // forest
  '#7c3aed', // purple
  '#00c896', // spring
  '#d81b60', // raspberry
  '#66b2ff', // powder blue
  '#ff6666', // salmon
  '#e0e0e0', // silver
];

/**
 * Deterministic palette index for a peer id. Colors used to be assigned by
 * arrival order per client, so every convoy member saw a different color for
 * the same car — "follow the yellow car" meant nothing. Hashing the peer id
 * gives every viewer the same color for the same peer within a session.
 * (Collisions are possible, but with more peers than palette entries they
 * were inevitable under arrival order too.)
 */
export function peerColorIndex(peerId: string, paletteSize = PEER_COLORS.length): number {
  let h = 0;
  for (let i = 0; i < peerId.length; i++) {
    h = (h * 31 + peerId.charCodeAt(i)) >>> 0;
  }
  return h % paletteSize;
}

export type MapOrientation = 'north' | 'heading';

export const MAP_ORIENTATION_STORAGE_KEY = 'teslawithme:mapOrientation';

/** Degrees clockwise from north. Heading-up rotates the map by the opposite amount. */
export function mapBearingDeg(orientation: MapOrientation, heading: number | null): number {
  if (orientation !== 'heading') return 0;
  if (heading == null || !Number.isFinite(heading)) return 0;
  return heading;
}

/**
 * CSS transform for the bearing wrapper. Empty when the map is north-up.
 * The wrapper fills the map container and spins around its own center,
 * the same pixel Leaflet uses as the view center. Rotating the tile and
 * marker panes does not: those panes are absolutely positioned with no
 * box of their own, so a 50% origin is the top-left corner and the car
 * swings off the crosshair as soon as the heading leaves north.
 */
export function mapBearingCss(bearingDeg: number): string {
  if (!Number.isFinite(bearingDeg) || bearingDeg === 0) return '';
  return `rotate(${-bearingDeg}deg)`;
}

/** Ignore GPS heading noise smaller than this so the map doesn't shiver. */
export const HEADING_DEADBAND_DEG = 8;

/** After a finger-drag, follow (and heading-up) comes back on its own. */
export const FOLLOW_RESUME_MS = 5_000;

/**
 * Hold the last shown heading until the new one has actually turned.
 * `next` may be outside 0–360; the value we keep is wrapped.
 */
export function stabilizeHeading(shown: number | null, next: number, minDeltaDeg = HEADING_DEADBAND_DEG): number {
  const normalized = ((next % 360) + 360) % 360;
  if (shown == null || !Number.isFinite(shown)) return normalized;
  const delta = Math.abs((((normalized - shown + 540) % 360) - 180));
  return delta < minDeltaDeg ? shown : normalized;
}

export function parseMapOrientation(value: string | null): MapOrientation {
  return value === 'heading' ? 'heading' : 'north';
}

export interface CarIconOptions {
  html: string;
  className: string;
  iconSize: [number, number];
  iconAnchor: [number, number];
  popupAnchor: [number, number];
  /** data URI for testing and non-Leaflet usage */
  iconUrl: string;
}

/** Top-down car pointing north (up). Rotate the inner SVG by GPS heading. */
export function makeCarIcon(color: string, shape: CarShape = DEFAULT_CAR_SHAPE): CarIconOptions {
  const svg = carMarkerSvg(shape, color);
  return {
    html: svg,
    className: 'car-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
    iconUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`,
  };
}

function createTileLayer(url: string, preset: TilePreset, attribution: string): L.TileLayer {
  return L.tileLayer(url, {
    maxZoom: preset.maxZoom,
    attribution,
    ...(preset.subdomains ? { subdomains: preset.subdomains } : {}),
    crossOrigin: true,
    updateWhenIdle: true,
    keepBuffer: 2,
  });
}

export class MapManager {
  private map: L.Map;
  private tileLayer: L.TileLayer;
  private overlayLayer: L.TileLayer | null = null;
  private currentStyle: Exclude<MapStyle, 'auto'>;
  private selfMarker: L.Marker | null = null;
  private peerMarkers = new Map<string, L.Marker>();
  private peerTrails = new Map<string, L.Polyline>();
  private selfTrail: L.Polyline | null = null;
  private trailWindowMs = 30 * 60 * 1000; // default 30 min
  // Latest raw trail samples kept per id so setTrailWindow can re-render
  // immediately without waiting for the next geo / peer update.
  private rawTrails = new Map<string, Array<{ lat: number; lng: number; timestamp: number }>>();
  private peerColors = new Map<string, string>();
  private selfColor = SELF_COLOR;
  private selfShape: CarShape = DEFAULT_CAR_SHAPE;
  private peerShapes = new Map<string, CarShape>();
  private destinationMarkers = new Map<string, L.Marker>();
  private peerLabels = new Map<string, string>();
  private stalePeers = new Set<string>();
  private followSelf = true;
  private orientation: MapOrientation = 'north';
  private selfHeading: number | null = null;
  private peerHeadings = new Map<string, number>();
  private onFollowChange: ((following: boolean) => void) | null = null;
  private bearingRotator: HTMLElement | null = null;
  private followResumeTimer: number | null = null;
  private resumeHeadingAfterDrag = false;

  constructor(containerId: string, initialStyle: Exclude<MapStyle, 'auto'> = 'light') {
    this.currentStyle = initialStyle;
    this.map = L.map(containerId, {
      zoomControl: true,
      attributionControl: false,
    }).setView([37.7749, -122.4194], 13);

    this.installBearingRotator();
    this.tileLayer = this.mountTiles(TILE_PRESETS[initialStyle]);

    L.control.attribution({ prefix: false }).addTo(this.map);

    this.map.on('dragstart', () => this.onMapDragStart());
  }

  setStyle(style: Exclude<MapStyle, 'auto'>): void {
    if (style === this.currentStyle) return;   // no-op if unchanged
    this.currentStyle = style;
    this.tileLayer = this.mountTiles(TILE_PRESETS[style]);
  }

  private mountTiles(preset: TilePreset): L.TileLayer {
    this.map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) layer.remove();
    });
    this.overlayLayer = null;
    const base = createTileLayer(preset.url, preset, preset.attribution).addTo(this.map);
    this.overlayLayer = preset.overlayUrl
      ? createTileLayer(preset.overlayUrl, preset, '').addTo(this.map)
      : null;
    return base;
  }

  updateSelf(update: {
    lat: number;
    lng: number;
    heading: number | null;
    trail: Array<{ lat: number; lng: number; timestamp: number }>;
  }): void {
    const latlng = L.latLng(update.lat, update.lng);

    if (update.heading != null && Number.isFinite(update.heading)) {
      this.selfHeading = stabilizeHeading(this.selfHeading, update.heading);
    }

    if (!this.selfMarker) {
      this.selfMarker = L.marker(latlng, {
        icon: L.divIcon(makeCarIcon(this.selfColor, this.selfShape)),
        zIndexOffset: 1000,
      })
        .bindPopup('You', { closeButton: false })
        .addTo(this.map);
      this.map.setView(latlng, Math.max(this.map.getZoom(), 15));
    } else {
      this.selfMarker.setLatLng(latlng);
    }
    this.applyMarkerHeading(this.selfMarker, this.selfHeading);

    if (this.followSelf) {
      this.map.panTo(latlng, { animate: false });
    }
    this.applyMapBearing();

    this.rawTrails.set('self', update.trail);
    this.updateTrail('self', update.trail, this.selfColor);
  }

  /** Set your own car color (a validated CAR_COLORS value). */
  setSelfColor(color: string): void {
    if (!isCarColor(color) || color === this.selfColor) return;
    this.selfColor = color;
    this.selfMarker?.setIcon(L.divIcon(makeCarIcon(color, this.selfShape)));
    if (this.selfMarker) this.applyMarkerHeading(this.selfMarker, this.selfHeading);
    this.selfTrail?.setStyle({ color });
  }

  /** Set your own body. Unknown values stay on the current shape. */
  setSelfShape(shape: CarShape): void {
    if (shape === this.selfShape) return;
    this.selfShape = shape;
    this.selfMarker?.setIcon(L.divIcon(makeCarIcon(this.selfColor, shape)));
    if (this.selfMarker) this.applyMarkerHeading(this.selfMarker, this.selfHeading);
  }

  updatePeer(msg: LocationMessage): void {
    const latlng = L.latLng(msg.lat, msg.lng);

    // A peer's declared color (validated against the whitelist) beats the
    // hash-assigned fallback. Live-update icon, trail, and destination pin
    // when it changes so a rename-and-recolor propagates without a rejoin.
    const declared = isCarColor(msg.color) ? msg.color : undefined;
    const previous = this.peerColors.get(msg.peerId);
    const color = declared ?? previous ?? PEER_COLORS[peerColorIndex(msg.peerId)]!;
    const knownShape = this.peerShapes.get(msg.peerId);
    const shape = isCarShape(msg.shape) ? msg.shape : (knownShape ?? 'plain');
    const shapeChanged = shape !== knownShape;
    this.peerShapes.set(msg.peerId, shape);
    if (msg.heading != null && Number.isFinite(msg.heading)) {
      this.peerHeadings.set(msg.peerId, msg.heading);
    }

    if (color !== previous || shapeChanged) {
      this.peerColors.set(msg.peerId, color);
      const marker = this.peerMarkers.get(msg.peerId);
      if (marker) {
        marker.setIcon(L.divIcon(makeCarIcon(color, shape)));
        this.applyMarkerHeading(marker, this.peerHeadings.get(msg.peerId) ?? null);
      }
      if (color !== previous) {
        this.peerTrails.get(msg.peerId)?.setStyle({ color });
        this.destinationMarkers.get(msg.peerId)?.setIcon(makeDestinationIcon(color));
      }
    }

    // Tap a car to see whose it is — essential once the convoy has more than
    // a handful of members and markers bunch up at lights.
    const label = escapeHtml(msg.name?.trim() || msg.peerId.slice(0, 8));
    if (!this.peerMarkers.has(msg.peerId)) {
      const marker = L.marker(latlng, {
        icon: L.divIcon(makeCarIcon(color, shape)),
        opacity: this.stalePeers.has(msg.peerId) ? STALE_MARKER_OPACITY : 1,
      })
        .bindPopup(label, { closeButton: false })
        .addTo(this.map);
      this.peerMarkers.set(msg.peerId, marker);
      this.peerLabels.set(msg.peerId, label);
      this.applyMarkerHeading(marker, this.peerHeadings.get(msg.peerId) ?? null);
    } else {
      const marker = this.peerMarkers.get(msg.peerId)!;
      marker.setLatLng(latlng);
      this.applyMarkerHeading(marker, this.peerHeadings.get(msg.peerId) ?? null);
      if (this.peerLabels.get(msg.peerId) !== label) {
        marker.setPopupContent(label);
        this.peerLabels.set(msg.peerId, label);
      }
    }

    const trail = rebaseTrailTimestamps(msg.trail);
    this.rawTrails.set(msg.peerId, trail);
    this.updateTrail(msg.peerId, trail, color);
  }

  removePeer(peerId: string): void {
    this.peerMarkers.get(peerId)?.remove();
    this.peerMarkers.delete(peerId);
    this.peerTrails.get(peerId)?.remove();
    this.peerTrails.delete(peerId);
    this.peerColors.delete(peerId);
    this.rawTrails.delete(peerId);
    this.peerLabels.delete(peerId);
    this.stalePeers.delete(peerId);
    this.peerHeadings.delete(peerId);
    this.peerShapes.delete(peerId);
    this.clearDestination(peerId);
  }

  updateDestination(peerId: string, lat: number, lng: number, label: string, color: string): void {
    const latlng = L.latLng(lat, lng);
    // label arrives over P2P from other peers — escape it or a hostile room
    // member can inject HTML/script into everyone's popups.
    const safeLabel = escapeHtml(label);
    const existing = this.destinationMarkers.get(peerId);
    if (existing) {
      existing.setLatLng(latlng);
      existing.setPopupContent(safeLabel);
    } else {
      const marker = L.marker(latlng, { icon: makeDestinationIcon(color), zIndexOffset: 500 })
        .bindPopup(safeLabel, { closeButton: false, className: 'dest-popup' })
        .addTo(this.map);
      this.destinationMarkers.set(peerId, marker);
    }
  }

  clearDestination(peerId: string): void {
    this.destinationMarkers.get(peerId)?.remove();
    this.destinationMarkers.delete(peerId);
  }

  getPeerColor(peerId: string): string {
    return this.peerColors.get(peerId) ?? '#888888';
  }

  /**
   * Visually dim a peer's marker and trail when their location has gone stale
   * (no update received for a while), and restore full opacity when fresh again.
   * The stale set is also consulted when markers/trails are (re)created, so the
   * styling survives element recreation (e.g. trail-window changes).
   */
  setPeerStale(peerId: string, stale: boolean): void {
    if (stale === this.stalePeers.has(peerId)) return;
    if (stale) this.stalePeers.add(peerId);
    else this.stalePeers.delete(peerId);
    this.peerMarkers.get(peerId)?.setOpacity(stale ? STALE_MARKER_OPACITY : 1);
    this.peerTrails.get(peerId)?.setStyle({
      opacity: stale ? STALE_TRAIL_OPACITY : TRAIL_OPACITY,
    });
  }

  panTo(lat: number, lng: number): void {
    this.cancelFollowResume();
    this.setFollow(false);
    this.map.setView(L.latLng(lat, lng), Math.max(this.map.getZoom(), 14));
  }

  isFollowing(): boolean {
    return this.followSelf;
  }

  getOrientation(): MapOrientation {
    return this.orientation;
  }

  setOnFollowChange(cb: (following: boolean) => void): void {
    this.onFollowChange = cb;
  }

  setFollow(follow: boolean): void {
    if (this.followSelf === follow) return;
    this.followSelf = follow;
    this.onFollowChange?.(follow);
  }

  setOrientation(orientation: MapOrientation): void {
    this.cancelFollowResume();
    this.applyOrientation(orientation);
  }

  setTrailWindow(minutes: 10 | 30 | 60): void {
    this.trailWindowMs = minutes * 60 * 1000;
    // Re-render any existing trails so the user sees the new window immediately
    // instead of waiting for the next geo / peer update.
    for (const [id, trail] of this.rawTrails) {
      const color = id === 'self' ? this.selfColor : (this.peerColors.get(id) ?? '#888888');
      this.updateTrail(id, trail, color);
    }
  }

  centerOnSelf(): void {
    const restoreHeading = this.resumeHeadingAfterDrag;
    this.cancelFollowResume();
    if (restoreHeading) {
      this.applyOrientation('heading');
      return;
    }
    this.setFollow(true);
    if (this.selfMarker) {
      this.map.setView(this.selfMarker.getLatLng(), Math.max(this.map.getZoom(), 15));
    }
    this.applyMapBearing();
  }

  /** Zoom/pan so every car in the room is on screen at once. */
  fitAll(): void {
    this.cancelFollowResume();
    this.setFollow(false);
    const points: L.LatLng[] = [];
    if (this.selfMarker) points.push(this.selfMarker.getLatLng());
    for (const marker of this.peerMarkers.values()) points.push(marker.getLatLng());
    if (points.length === 0) return;
    if (points.length === 1) {
      this.map.setView(points[0]!, Math.max(this.map.getZoom(), 13));
      return;
    }
    this.map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: 15 });
  }

  destroy(): void {
    this.cancelFollowResume();
    this.map.remove();
    this.bearingRotator?.remove();
    this.bearingRotator = null;
  }

  private applyOrientation(orientation: MapOrientation): void {
    if (this.orientation === orientation) {
      this.applyMapBearing();
      return;
    }
    this.orientation = orientation;
    if (orientation === 'heading') this.setFollow(true);
    if (this.followSelf && this.selfMarker) {
      this.map.panTo(this.selfMarker.getLatLng(), { animate: false });
    }
    this.applyMapBearing();
    this.onFollowChange?.(this.followSelf);
  }

  private onMapDragStart(): void {
    const restoreHeading = this.orientation === 'heading';
    this.cancelFollowResume();
    this.setFollow(false);
    if (restoreHeading) this.applyOrientation('north');
    this.resumeHeadingAfterDrag = restoreHeading;
    this.followResumeTimer = window.setTimeout(() => {
      this.followResumeTimer = null;
      const restore = this.resumeHeadingAfterDrag;
      this.resumeHeadingAfterDrag = false;
      if (restore) this.applyOrientation('heading');
      else this.centerOnSelf();
    }, FOLLOW_RESUME_MS);
  }

  private cancelFollowResume(): void {
    if (this.followResumeTimer !== null) {
      window.clearTimeout(this.followResumeTimer);
      this.followResumeTimer = null;
    }
    this.resumeHeadingAfterDrag = false;
  }

  private installBearingRotator(): void {
    const mapPane = this.map.getPane('mapPane');
    if (!mapPane) return;
    const rotator = document.createElement('div');
    rotator.className = 'map-bearing-rotator';
    // Inline, not just CSS: the pivot has to be the container center even
    // if the stylesheet hasn't applied yet. Percentage origin on a 0×0 pane
    // is the corner, which is what threw the car off center.
    rotator.style.position = 'absolute';
    rotator.style.left = '0';
    rotator.style.top = '0';
    rotator.style.width = '100%';
    rotator.style.height = '100%';
    rotator.style.transformOrigin = 'center center';
    rotator.style.pointerEvents = 'none';
    mapPane.parentElement?.insertBefore(rotator, mapPane);
    rotator.appendChild(mapPane);
    this.bearingRotator = rotator;
  }

  private applyMarkerHeading(marker: L.Marker, heading: number | null): void {
    const el = marker.getElement();
    const svg = el?.querySelector<SVGElement>('.car-icon-svg');
    if (!svg) return;
    const deg = heading != null && Number.isFinite(heading) ? heading : 0;
    svg.style.transformOrigin = '16px 16px';
    svg.style.transform = `rotate(${deg}deg)`;
  }

  private applyMapBearing(): void {
    if (!this.bearingRotator) return;
    this.bearingRotator.style.transform = mapBearingCss(
      mapBearingDeg(this.orientation, this.selfHeading),
    );
  }

  private updateTrail(
    id: string,
    trail: Array<{ lat: number; lng: number; timestamp: number }>,
    color: string,
  ): void {
    // Timestamps are normalized to the local wall clock before they reach
    // here (self entries are stamped with Date.now() in GeoManager; peer
    // trails go through rebaseTrailTimestamps in updatePeer), so a plain
    // wall-clock cutoff both handles alien epochs and lets trails age out
    // in real time while a car sits parked.
    const cutoff = Date.now() - this.trailWindowMs;
    const points = trail
      .filter(p => p.timestamp >= cutoff)
      .map(p => L.latLng(p.lat, p.lng));

    const existing = id === 'self' ? this.selfTrail : this.peerTrails.get(id);

    if (points.length < 2) {
      existing?.remove();
      if (id === 'self') this.selfTrail = null;
      else this.peerTrails.delete(id);
      return;
    }

    if (existing) {
      existing.setLatLngs(points);
    } else {
      const opacity = this.stalePeers.has(id) ? STALE_TRAIL_OPACITY : TRAIL_OPACITY;
      const polyline = L.polyline(points, { color, weight: 3, opacity }).addTo(this.map);
      if (id === 'self') this.selfTrail = polyline;
      else this.peerTrails.set(id, polyline);
    }
  }
}
